import { useState } from 'react';
import { Home, Search, Filter } from 'lucide-react';
import { useProjets } from '../hooks/usePublicApi';
import { useTexts } from '../hooks/useTexts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import ProjectCard from '../components/common/ProjectCard';

export default function ProjectsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('name');

  // Récupération des données depuis l'API
  const { data: projets, loading, error } = useProjets();
  
  // Hook pour les textes dynamiques
  const { getText } = useTexts();

  // Filtrage et tri des projets
  const filteredProjects = (projets || [])
    .filter(project => 
      project.nom_projet.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.adresse.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(project => 
      statusFilter === 'ALL' || project.statut_projet === statusFilter
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.nom_projet.localeCompare(b.nom_projet);
        case 'price':
          return parseFloat(a.budget_total) - parseFloat(b.budget_total);
        case 'date':
          return new Date(b.date_lancement).getTime() - new Date(a.date_lancement).getTime();
        default:
          return 0;
      }
    });

  // Gestion des états de chargement et d'erreur
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ErrorMessage 
          error={error} 
          onDismiss={() => window.location.reload()} 
        />
      </div>
    );
  }

  const statusOptions = [
    { value: 'ALL', label: getText('projects.filter.all_statuses') },
    { value: 'A_VENIR', label: getText('projects.filter.coming_soon') },
    { value: 'EN_COURS', label: getText('projects.filter.in_progress') },
    { value: 'TERMINE', label: getText('projects.filter.completed') },
  ];

  const sortOptions = [
    { value: 'name', label: getText('projects.sort.name') },
    { value: 'price', label: getText('projects.sort.price') },
    { value: 'date', label: getText('projects.sort.date') },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {getText('projects.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {getText('projects.description')}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={getText('projects.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="relative">
              <Filter className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {getText('projects.sort.by')} {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            {filteredProjects.length} {filteredProjects.length > 1 ? getText('projects.results.count_plural') : getText('projects.results.count')}
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>

        {/* No Results */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Home className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {getText('projects.empty.title')}
            </h3>
            <p className="text-gray-600 mb-4">
              {getText('projects.empty.description')}
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('ALL');
                setSortBy('name');
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {getText('projects.empty.reset')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}